import s from './Template.module.sass'
import React, { FC, useRef } from 'react'
import Link from 'next/link'

import { Button } from '@shared/ui/Actions/Button'
import { CardTwoBlock } from '@shared/ui/Layout/CardTwoBlock'
import { EGG } from '@shared/api/analytics'
import { IconButton } from '@shared/ui/Actions/IconButton'
import { Rating } from '@shared/ui/Feedback/Rating'
import { Text } from '@shared/ui/Typography/Text'
import { useCurrentSize } from '@shared/lib/helpers/classes'

import IconLike from '@icons/icon-like.svg'
import IconChevron from '@icons/icon-chevron-medium.svg'

import { openBooking } from '@widgets/Restaurant/Booking'
import { useAppDispatch, useAppSelector } from '@app/model/store'
import { TemplateCardProps } from '@widgets/Restaurant/RestCards/RestCard/type/TemplateCardProps'
import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { useIntersectionAnalytic } from '@shared/lib/hooks/useIntersectionAnalytic'
import { Favorite } from '@features/Favorite'
import {
  sendAdvertisingRestAnalytic,
  TypeAdvertisingCardRestEvent,
} from '@shared/api/analytics/advertising/advertising'
import { ChevronWithTooltip } from '@shared/ui/Feedback/ChevronWithTooltip'
import TitleKuper from '@icons/kuper/title-kuper.svg'
import AdvDetailed from '@features/Common/AdvDetailed/AdvDetailed'
import { AdvertBadge } from '@shared/ui/Feedback/AdvertBadge'

const sizeTitle = {
  L: 'L',
  ML: 'ML',
  M: 'M',
  S: 'M',
}

type Props = TemplateCardProps & React.DetailedHTMLProps<React.HTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>

const Template: FC<Props> = ({
  type,
  data,
  analytic,
  sizes,
  send_analytic = true,
  advert,
  onClickInsteadLink,
  className = '',
  children,
  additionalData,
  ...props
}) => {
  const [size] = useCurrentSize(sizes)
  const cardRef = useRef<HTMLAnchorElement>(null)
  const dispatch = useAppDispatch()

  const handleVisibleCard = () => {
    if (send_analytic) {
      EGG.entity.restaurant.card_view(data, {
        section_name: analytic.section_name,
        section_index: analytic.section_index,
      })
      handleAnalyticAdvert('visible')
    }
  }

  useIntersectionAnalytic(cardRef, handleVisibleCard)

  const handleAnalyticAdvert = (typeEvent: TypeAdvertisingCardRestEvent) => {
    if (data.categories.advert && analytic?.advertCardPlaceType && send_analytic) {
      sendAdvertisingRestAnalytic({
        type: typeEvent,
        city_id: data.address.city.id,
        rest_id: data.id,
        variety: analytic.advertCardPlaceType,
      })
    }
  }

  const handleClickCard = () => {
    if (send_analytic) {
      EGG.entity.restaurant.card_click(data, {
        section_name: analytic.section_name,
        section_index: analytic.section_index,
      })
      handleAnalyticAdvert('click')
    }
  }

  const RatePromoTemplate = () => {
    if (type === 'gig-assist') return <div></div>

    if (data.categories.advert) return <Rating isPromo sizes={'S'} className={size === 'S' ? s['promo-label'] : ''} />
    else if (data.feedback.rating && data.feedback.rating >= 4)
      return <Rating rating={Number(data.feedback.rating)} sizes={'S'} />
    else return <div></div>
  }

  const handleBooking = (event) => {
    event.stopPropagation()
    event.preventDefault()

    const dataAfisha = data.booking.afisha
    const dataTomesto = data.booking.tomesto

    if (dataAfisha.active && type !== 'gig-assist') {
      dispatch(openBooking(data))
    } else if (dataAfisha.active && type === 'gig-assist') {
      window.open(`${data.url.path}/?reserve`, '_blank')
    } else if (dataTomesto.active) {
      window.open(dataTomesto.url, '_blank')
    }

    if (send_analytic) {
      EGG.entity.restaurant.click_booking(data, {
        section_name: analytic.section_name,
        section_index: analytic.section_index,
      })
    }
  }

  const thisImage = data.images.main || data.images.cover.rest1064x1064

  let bookingInfoClass = null
  if (data?.booking?.afisha?.active) {
    bookingInfoClass = 'booking-afisha'
  } else if (data?.booking?.tomesto?.active) {
    bookingInfoClass = 'booking-tomesto'
  }

  const isShowKuper = !!advert?.kuper
  const isShowBookingButton =
    type !== 'booking' && (data.booking.afisha.active || data.booking.tomesto.active) && !isShowKuper
  const isTrimHeader = type === 'booking'
  const isShowChevron = type !== 'gig-assist' && data.categories.selection

  // TODO: CardTwoBlock — choose better naming
  return (
    <ErrorBoundary>
      <CardTwoBlock
        sizes={sizes}
        href={data.url.path}
        className={`${s.card} ${className}`}
        onClick={handleClickCard}
        // @ts-ignore
        ref={cardRef}
        title={data.name}
        {...props}>
        <CardTwoBlock.Img
          img={thisImage}
          restId={String(data.id)}
          alt={''}
          classNameContent={s['img-content']}
          onClickInsteadLink={onClickInsteadLink}>
          <RatePromoTemplate />

          {isShowChevron && <ChevronWithTooltip icon={IconChevron} className={s['afisha-selected']} />}

          <div className={s['img-bottom-wrapper']}>
            {isShowBookingButton && (
              <Button
                className={`${s['img-booking-button']} ${bookingInfoClass}`}
                sizes={'S'}
                mode={'white'}
                onClick={handleBooking}>
                Забронировать
              </Button>
            )}

            {isShowKuper && (
              <Link href={advert.kuper.link}>
                <Button className={s['kuper-button']} sizes={'S'} mode={'white'} onClick={handleBooking}>
                  <TitleKuper className={s['kuper-icon']} />
                  <AdvertBadge className={s['kuper-badge']} sizes={'42x18'} />
                  <AdvDetailed>
                    <AdvDetailed.Button className={s['kuper-info-button']} />
                    <AdvDetailed.Popup description={advert.kuper.description} token={advert.kuper.token} />
                  </AdvDetailed>
                </Button>
              </Link>
            )}

            <Favorite
              category={'restaurant'}
              id={data.id}
              analytic={{ data, other: { section_name: analytic?.section_name } }}>
              <IconButton
                sizes={'S'}
                mode={'white'}
                className={s.favorite}
                // data-active={isActiveFavoriteIcon}
                icon={IconLike}
              />
            </Favorite>
          </div>
        </CardTwoBlock.Img>

        <CardTwoBlock.Content data-promo={type !== 'gig-assist' && data.categories.advert} className={s.content}>
          {onClickInsteadLink ? (
            <Text
              id={'rest-card-title'}
              tag={'span'}
              sizes={sizeTitle[size]}
              className={s.title}
              data-trim={isTrimHeader}
              onClick={onClickInsteadLink}>
              {data.name}
            </Text>
          ) : (
            <Link href={data.url.path} scroll={false}>
              <Text
                id={'rest-card-title'}
                tag={'span'}
                sizes={sizeTitle[size]}
                className={s.title}
                data-trim={isTrimHeader}>
                {data.name}
              </Text>
            </Link>
          )}

          {children}
        </CardTwoBlock.Content>
      </CardTwoBlock>
    </ErrorBoundary>
  )
}

export default Template
